import React, { useState, useMemo, useRef, useEffect } from 'react';
import { GameState, Player, TeamInLeague, SearchHistoryItem } from '../../domain/entities';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { iconMap } from '../../utils/iconUtils';
import './GlobalSearch.css';

interface GlobalSearchProps {
    gameState: GameState;
    isOpen: boolean;
    onClose: () => void;
    onNavigateToPlayer: (playerId: number) => void;
    onNavigateToTeam: (teamName: string) => void;
    onNavigateToLeague: (leagueName: string) => void;
    onNavigateToPage: (page: string) => void;
    onNavigateToCompetition: (cupName: string) => void;
    onAddToSearchHistory: (item: SearchHistoryItem) => void;
}

export const GlobalSearch: React.FC<GlobalSearchProps> = ({
    gameState,
    isOpen,
    onClose,
    onNavigateToPlayer,
    onNavigateToTeam,
    onNavigateToLeague,
    onNavigateToPage,
    onNavigateToCompetition,
    onAddToSearchHistory,
}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedIndex, setSelectedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);

    // Available pages for navigation
    const pagesList = ['Dashboard', 'Squad', 'Tactics', 'Fixtures', 'Transfers', 'Finances', 'Youth Academy', 'Inbox'];

    // Get all searchable items from game state
    const { players, teams, leagues, pages, competitions } = useMemo(() => {
        const allPlayers: Array<{ player: Player; teamName: string }> = [];
        const allTeams: TeamInLeague[] = [];
        const allLeagues = Object.entries(gameState.leagues).map(([name]) => name);
        const allPages = pagesList;
        const allCompetitions = gameState.config?.CUP_NAMES || [];

        Object.values(gameState.leagues).forEach(league => {
            league.teams.forEach(team => {
                allTeams.push(team);
                team.squad.forEach(player => {
                    allPlayers.push({ player, teamName: team.name });
                });
            });
        });

        return {
            players: allPlayers,
            teams: allTeams,
            leagues: allLeagues,
            pages: allPages,
            competitions: allCompetitions
        };
    }, [gameState]);

    // Search results
    const results = useMemo(() => {
        if (!searchQuery.trim()) return { players: [], teams: [], leagues: [], pages: [], competitions: [] };

        const query = searchQuery.toLowerCase();
        const searchTerms = query.split(/\s+/).filter(term => term.length > 0);

        // Search players with scoring
        const playerMatches = players
            .map(({ player, teamName }) => {
                const playerNameLower = player.name.toLowerCase();
                const teamNameLower = teamName.toLowerCase();

                let score = 0;

                if (searchTerms.length === 1) {
                    const term = searchTerms[0];
                    if (playerNameLower.includes(term)) {
                        score = 1000;
                    } else if (teamNameLower.includes(term)) {
                        score = 50;
                    }
                } else {
                    const playerMatches = searchTerms.filter(term => playerNameLower.includes(term));
                    const teamMatches = searchTerms.filter(term => teamNameLower.includes(term));

                    if (playerMatches.length + teamMatches.length === searchTerms.length) {
                        if (playerMatches.length > 0) {
                            score = 500;
                        } else {
                            score = 75;
                        }
                    }
                }

                return { player, teamName, score };
            })
            .filter(result => result.score > 0)
            .sort((a, b) => b.score - a.score)
            .slice(0, 8);

        // Search teams (max 5)
        const matchedTeams = teams
            .filter(team => team.name.toLowerCase().includes(query))
            .slice(0, 5);

        // Search leagues (max 3)
        const matchedLeagues = leagues
            .filter(league => league.toLowerCase().includes(query))
            .slice(0, 3);

        // Search pages (max 3)
        const matchedPages = pages
            .filter(page => page.toLowerCase().includes(query))
            .slice(0, 3);

        // Search competitions (max 3)
        const matchedCompetitions = competitions
            .filter(comp => comp.toLowerCase().includes(query))
            .slice(0, 3);

        return {
            players: playerMatches.map(({ player, teamName }) => ({ player, teamName })),
            teams: matchedTeams,
            leagues: matchedLeagues,
            pages: matchedPages,
            competitions: matchedCompetitions
        };
    }, [searchQuery, players, teams, leagues, pages, competitions]);

    const totalResults = results.players.length + results.teams.length + results.leagues.length + results.pages.length + results.competitions.length;

    // Get MRU (Most Recently Used) items - max 3
    const mruItems = useMemo(() => {
        return (gameState.searchHistory || []).slice(0, 3);
    }, [gameState.searchHistory]);

    // Focus input when modal opens
    useEffect(() => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
            setSearchQuery('');
            setSelectedIndex(-1);
        }
    }, [isOpen]);

    // Handle keyboard navigation
    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            onClose();
            return;
        }

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSelectedIndex(prev =>
                prev < totalResults - 1 ? prev + 1 : prev
            );
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        } else if (e.key === 'Enter' && selectedIndex >= 0) {
            e.preventDefault();
            handleSelectResult(selectedIndex);
        }
    };

    const handleSelectResult = (index: number) => {
        let selectedItem: SearchHistoryItem | null = null;

        if (index < results.players.length) {
            const selected = results.players[index];
            onNavigateToPlayer(selected.player.id);
            selectedItem = {
                type: 'player',
                id: selected.player.id.toString(),
                name: selected.player.name,
                timestamp: Date.now()
            };
        } else {
            let offset = results.players.length;

            // Teams
            if (index < offset + results.teams.length) {
                const selected = results.teams[index - offset];
                onNavigateToTeam(selected.name);
                selectedItem = {
                    type: 'team',
                    id: selected.name,
                    name: selected.name,
                    timestamp: Date.now()
                };
            } else {
                offset += results.teams.length;

                // Leagues
                if (index < offset + results.leagues.length) {
                    const selected = results.leagues[index - offset];
                    onNavigateToLeague(selected);
                    selectedItem = {
                        type: 'league',
                        id: selected,
                        name: selected,
                        timestamp: Date.now()
                    };
                } else {
                    offset += results.leagues.length;

                    // Pages
                    if (index < offset + results.pages.length) {
                        const selected = results.pages[index - offset];
                        onNavigateToPage(selected);
                        selectedItem = {
                            type: 'page',
                            id: selected.toLowerCase().replace(/\s+/g, '-'),
                            name: selected,
                            timestamp: Date.now()
                        };
                    } else {
                        offset += results.pages.length;

                        // Competitions
                        if (index < offset + results.competitions.length) {
                            const selected = results.competitions[index - offset];
                            onNavigateToCompetition(selected);
                            selectedItem = {
                                type: 'competition',
                                id: selected,
                                name: selected,
                                timestamp: Date.now()
                            };
                        }
                    }
                }
            }
        }

        if (selectedItem) {
            onAddToSearchHistory(selectedItem);
        }
        onClose();
    };

    // Handle MRU item click
    const handleMRUClick = (item: SearchHistoryItem) => {
        onAddToSearchHistory(item);

        switch (item.type) {
            case 'player':
                onNavigateToPlayer(parseInt(item.id));
                break;
            case 'team':
                onNavigateToTeam(item.id);
                break;
            case 'league':
                onNavigateToLeague(item.id);
                break;
            case 'page':
                onNavigateToPage(item.name);
                break;
            case 'competition':
                onNavigateToCompetition(item.id);
                break;
        }

        onClose();
    };

    if (!isOpen) return null;

    // Calculate result indices for keyboard navigation
    let resultIndex = 0;
    const getResultIndex = (category: 'players' | 'teams' | 'leagues' | 'pages' | 'competitions', itemIndex: number) => {
        let offset = 0;
        if (category !== 'players') offset += results.players.length;
        if (category !== 'teams') offset += results.teams.length;
        if (category !== 'leagues') offset += results.leagues.length;
        if (category !== 'pages') offset += results.pages.length;
        // competitions is last, no additional offset needed
        return offset + itemIndex;
    };

    return (
        <div className="global-search-overlay" onClick={onClose}>
            <div className="global-search-container" onClick={e => e.stopPropagation()}>
                <div className="global-search-input-wrapper">
                    <FontAwesomeIcon icon={iconMap['search']} className="global-search-icon" />
                    <input
                        ref={inputRef}
                        type="text"
                        placeholder="Search players, teams, leagues, pages..."
                        className="global-search-input"
                        value={searchQuery}
                        onChange={e => {
                            setSearchQuery(e.target.value);
                            setSelectedIndex(-1);
                        }}
                        onKeyDown={handleKeyDown}
                    />
                    {searchQuery && (
                        <button
                            className="global-search-clear"
                            onClick={() => {
                                setSearchQuery('');
                                setSelectedIndex(-1);
                            }}
                            aria-label="Clear search"
                        >
                            ✕
                        </button>
                    )}
                </div>

                {/* MRU Section - Show when no search query */}
                {!searchQuery && mruItems.length > 0 && (
                    <div className="global-search-mru">
                        <div className="global-search-section-title" style={{ padding: 'var(--spacing-sm) var(--spacing-lg)', marginBottom: 0 }}>Recently Visited</div>
                        <div className="global-search-mru-items">
                            {mruItems.map((item, index) => (
                                <button
                                    key={`${item.type}-${item.id}`}
                                    className="global-search-mru-item"
                                    onClick={() => handleMRUClick(item)}
                                    onMouseEnter={() => setSelectedIndex(index)}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--spacing-sm)',
                                        padding: 'var(--spacing-sm) var(--spacing-lg)',
                                        width: '100%',
                                        textAlign: 'left',
                                        background: selectedIndex === index ? 'var(--accent-color-rgb)' : 'transparent',
                                        color: selectedIndex === index ? 'var(--secondary-accent-color)' : 'var(--text-color)',
                                        border: 'none',
                                        cursor: 'pointer',
                                        transition: 'all 0.1s ease'
                                    }}
                                >
                                    <span style={{ fontSize: '0.85em', opacity: 0.7 }}>{item.type}</span>
                                    <span style={{ fontWeight: 500 }}>{item.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                )}

                {searchQuery && totalResults === 0 ? (
                    <div className="global-search-empty">
                        <p>No results found for "{searchQuery}"</p>
                    </div>
                ) : searchQuery ? (
                    <div className="global-search-results">
                        {results.players.length > 0 && (
                            <div className="global-search-section">
                                <div className="global-search-section-title">Players</div>
                                {results.players.map(({ player, teamName }, index) => (
                                    <button
                                        key={player.id}
                                        className={`global-search-result-item ${selectedIndex === index ? 'selected' : ''}`}
                                        onClick={() => handleSelectResult(index)}
                                        onMouseEnter={() => setSelectedIndex(index)}
                                    >
                                        <div className="result-info">
                                            <div className="result-name">{player.name}</div>
                                            <div className="result-meta">
                                                <span className="result-position">{player.position}</span>
                                                <span className="result-skill">Skill {player.skill}</span>
                                                <span className="result-team">{teamName}</span>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}

                        {results.teams.length > 0 && (
                            <div className="global-search-section">
                                <div className="global-search-section-title">Teams</div>
                                {results.teams.map((team, index) => {
                                    const resultIdx = results.players.length + index;
                                    return (
                                        <button
                                            key={team.name}
                                            className={`global-search-result-item ${selectedIndex === resultIdx ? 'selected' : ''}`}
                                            onClick={() => handleSelectResult(resultIdx)}
                                            onMouseEnter={() => setSelectedIndex(resultIdx)}
                                        >
                                            <div className="result-info">
                                                <div className="result-name">{team.name}</div>
                                                <div className="result-meta">
                                                    <span className="result-squad">{team.squad.length} Players</span>
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {results.leagues.length > 0 && (
                            <div className="global-search-section">
                                <div className="global-search-section-title">Leagues</div>
                                {results.leagues.map((league, index) => {
                                    const resultIdx = results.players.length + results.teams.length + index;
                                    return (
                                        <button
                                            key={league}
                                            className={`global-search-result-item ${selectedIndex === resultIdx ? 'selected' : ''}`}
                                            onClick={() => handleSelectResult(resultIdx)}
                                            onMouseEnter={() => setSelectedIndex(resultIdx)}
                                        >
                                            <div className="result-info">
                                                <div className="result-name">{league}</div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {results.pages.length > 0 && (
                            <div className="global-search-section">
                                <div className="global-search-section-title">Pages</div>
                                {results.pages.map((page, index) => {
                                    const resultIdx = results.players.length + results.teams.length + results.leagues.length + index;
                                    return (
                                        <button
                                            key={page}
                                            className={`global-search-result-item ${selectedIndex === resultIdx ? 'selected' : ''}`}
                                            onClick={() => handleSelectResult(resultIdx)}
                                            onMouseEnter={() => setSelectedIndex(resultIdx)}
                                        >
                                            <div className="result-info">
                                                <div className="result-name">{page}</div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {results.competitions.length > 0 && (
                            <div className="global-search-section">
                                <div className="global-search-section-title">Competitions</div>
                                {results.competitions.map((comp, index) => {
                                    const resultIdx = results.players.length + results.teams.length + results.leagues.length + results.pages.length + index;
                                    return (
                                        <button
                                            key={comp}
                                            className={`global-search-result-item ${selectedIndex === resultIdx ? 'selected' : ''}`}
                                            onClick={() => handleSelectResult(resultIdx)}
                                            onMouseEnter={() => setSelectedIndex(resultIdx)}
                                        >
                                            <div className="result-info">
                                                <div className="result-name">{comp}</div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="global-search-hint">
                        <p>Search by player, team, league, page, or competition</p>
                        <p style={{ fontSize: '0.85em', opacity: 0.7, marginTop: '4px' }}>e.g. "smith", "united", "premier", "squad"</p>
                    </div>
                )}
            </div>
        </div>
    );
};
